import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { HoursList } from '@/pages/public/booking-page/components/HoursList';
import { InfoNote, Segmented, SettingsCard, ToggleRow } from './primitives';
import { HoursEditor } from './HoursEditor';
import type { BookingPageAdmin, BookingPageSettings } from '../api';
import { customHoursToOpening } from '../draft';

interface SectionsCardProps {
  draft: BookingPageSettings;
  data: BookingPageAdmin;
  timeZone?: string;
  onChange: (patch: Partial<BookingPageSettings>) => void;
}

export function SectionsCard({ draft, data, timeZone, onChange }: SectionsCardProps) {
  const { t } = useTranslation('adminBookingPage');
  const [hoursSheet, setHoursSheet] = useState(false);
  const address = data.organization.address;
  const phone = data.organization.phone;

  const hoursBlock = (
    <div className="flex flex-col gap-3">
      <Segmented
        label={t('sections.hours')}
        value={draft.hours_source}
        onChange={(hours_source) => onChange({ hours_source })}
        options={[
          { value: 'team', label: t('sections.hoursTeam') },
          { value: 'custom', label: t('sections.hoursCustom') },
        ]}
      />
      {draft.hours_source === 'team' ? (
        <>
          <div className="rounded-xl border border-line bg-bg-public px-4 py-2.5" data-testid="team-hours">
            <HoursList hours={data.preview_source.team_hours} timeZone={timeZone} />
          </div>
          <InfoNote>{t('sections.hoursTeamInfo')}</InfoNote>
        </>
      ) : (
        <>
          <div className="hidden rounded-xl border border-line bg-bg-public px-4 py-1 sm:block">
            <HoursEditor value={draft.custom_hours} onChange={(custom_hours) => onChange({ custom_hours })} />
          </div>
          <div className="flex flex-col gap-3 sm:hidden">
            <div className="rounded-xl border border-line bg-bg-public px-4 py-2.5">
              <HoursList hours={customHoursToOpening(draft.custom_hours, timeZone)} timeZone={timeZone} />
            </div>
            <Button variant="outline" className="h-11" onClick={() => setHoursSheet(true)}>{t('sections.editHours')}</Button>
          </div>
          <InfoNote>{t('sections.hoursCustomInfo')}</InfoNote>
        </>
      )}
    </div>
  );

  return (
    <SettingsCard title={t('sections.title')} description={t('sections.description')} testId="sections-card">
      <div className="flex flex-col">
        <ToggleRow id="hours" first label={t('sections.hours')} hint={t('sections.hoursHint')} checked={draft.show_hours} onChange={(v) => onChange({ show_hours: v })}>
          {draft.show_hours && hoursBlock}
        </ToggleRow>
        <ToggleRow id="address" label={t('sections.address')} hint={t('sections.addressHint')} checked={draft.show_address} onChange={(v) => onChange({ show_address: v })}>
          {draft.show_address && (
            address ? (
              <div className="flex flex-col gap-3">
                <span className="inline-flex items-start gap-2 text-body font-medium text-ink"><MapPin className="mt-0.5 h-4 w-4 text-soft" />{address}</span>
                {!data.organization.has_coordinates && <InfoNote>{t('sections.notGeocoded')}</InfoNote>}
                <div className="rounded-xl border border-line px-4">
                  <ToggleRow id="map" first label={t('sections.showMap')} hint={t('sections.showMapHint')} checked={draft.show_map} onChange={(v) => onChange({ show_map: v })} />
                  <ToggleRow id="directions" label={t('sections.showDirections')} hint={t('sections.showDirectionsHint')} checked={draft.show_directions} onChange={(v) => onChange({ show_directions: v })} />
                </div>
              </div>
            ) : (
              <InfoNote>{t('sections.noAddress')}</InfoNote>
            )
          )}
        </ToggleRow>
        <ToggleRow id="online" label={t('sections.online')} hint={t('sections.onlineHint')} checked={draft.show_online} onChange={(v) => onChange({ show_online: v })} />
        <ToggleRow
          id="team"
          label={t('sections.team')}
          hint={t('sections.teamHint')}
          control={<span className="hidden sm:block"><Segmented label={t('sections.team')} value={draft.team_mode} onChange={(team_mode) => onChange({ team_mode })} options={[{ value: 'auto', label: t('sections.teamAuto') }, { value: 'show', label: t('sections.teamShow') }, { value: 'hide', label: t('sections.teamHide') }]} /></span>}
        >
          <div className="sm:hidden">
            <Segmented grow label={t('sections.team')} value={draft.team_mode} onChange={(team_mode) => onChange({ team_mode })} options={[{ value: 'auto', label: t('sections.teamAuto') }, { value: 'show', label: t('sections.teamShow') }, { value: 'hide', label: t('sections.teamHide') }]} />
          </div>
        </ToggleRow>
        <ToggleRow id="phone" label={t('sections.phone')} hint={phone ? t('sections.phoneHint', { phone }) : t('sections.phoneEmpty')} checked={draft.show_phone} onChange={(v) => onChange({ show_phone: v })} />
      </div>

      <Sheet open={hoursSheet} onOpenChange={setHoursSheet}>
        <SheetContent side="bottom" className="flex h-[100dvh] flex-col gap-4 overflow-y-auto bg-bg-app p-4">
          <SheetTitle className="font-display text-[18px]">{t('sections.hours')}</SheetTitle>
          <div className="rounded-2xl border border-line bg-surface px-4 py-1">
            <HoursEditor value={draft.custom_hours} onChange={(custom_hours) => onChange({ custom_hours })} />
          </div>
          <InfoNote>{t('sections.hoursCustomInfo')}</InfoNote>
          <div className="sticky bottom-0 mt-auto pb-[env(safe-area-inset-bottom)]">
            <Button className="h-14 w-full rounded-lg text-[16px]" onClick={() => setHoursSheet(false)}>{t('sections.done')}</Button>
          </div>
        </SheetContent>
      </Sheet>
    </SettingsCard>
  );
}
